import { Link, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ChevronLeft, Download, Loader2, Lock } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import logoImage from '@/assets/OKYC logo.webp';
import { Button } from '@/components/ui/button';
import { useCurrentBalance } from '@/features/payments';
import { UserMenu } from '@/layouts/components/user-menu';
import { cn } from '@/lib/utils';
import { downloadBasicReportPdf, useBasicReport } from '../api/risk-assessment-wizard';
import { CheckStatusTable } from './check-status-table';

interface BasicReportPageProps {
  assessmentId: string;
  onUpgrade: () => void;
}

const RISK_LEVEL_STYLES: Record<'LOW' | 'MEDIUM' | 'HIGH', string> = {
  LOW: 'bg-green-100 text-green-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  HIGH: 'bg-red-100 text-red-800',
};

export function BasicReportPage({ assessmentId, onUpgrade }: BasicReportPageProps) {
  const navigate = useNavigate();
  const { data: basicReport, isLoading, error } = useBasicReport(assessmentId);
  const { data: balanceData } = useCurrentBalance();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      await downloadBasicReportPdf(assessmentId);
      toast.success('PDF downloaded successfully');
    } catch (err) {
      console.error('Failed to download PDF:', err);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !basicReport) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-gray-50">
        <p className="text-lg text-gray-600">Failed to load report</p>
        <Button variant="outline" onClick={() => navigate({ to: '/' })}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const { clientDetails, riskLevel, checkStatuses, dateGenerated } = basicReport;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
      {/* Header */}
      <header className="flex h-16 shrink-0 items-center justify-between border-b bg-white px-6">
        <div className="flex items-center gap-2">
          <Link to="/">
            <img src={logoImage} alt="OKYC" className="h-8" />
          </Link>
          <button
            type="button"
            onClick={() => navigate({ to: '/' })}
            className="flex items-center text-gray-600 hover:text-gray-900"
            aria-label="Go back to dashboard"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[14px] text-[#051923]">
            Balance: {balanceData?.balanceFormatted ?? '£0.00'}
          </span>
          <UserMenu />
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-8 py-8">
          {/* Report Title & Risk Badge */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-[#051923]">Screening Summary</h1>
              {dateGenerated && (
                <p className="mt-1 text-sm text-gray-500">
                  Generated {format(new Date(dateGenerated), 'dd MMMM yyyy, HH:mm')}
                </p>
              )}
            </div>
            <div
              className={cn(
                'rounded-full px-4 py-1.5 text-sm font-medium',
                RISK_LEVEL_STYLES[riskLevel],
              )}
            >
              {riskLevel} Risk
            </div>
          </div>

          {/* Client Details Section */}
          <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-[#051923]">Client Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Client Reference</p>
                <p className="font-medium">{clientDetails.clientRef || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="font-medium">{clientDetails.fullName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date of Birth</p>
                <p className="font-medium">
                  {clientDetails.dateOfBirth
                    ? format(new Date(clientDetails.dateOfBirth), 'dd/MM/yyyy')
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Nationality</p>
                <p className="font-medium">{clientDetails.nationality || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Occupation/Source of Income</p>
                <p className="whitespace-pre-line font-medium">
                  {clientDetails.occupation || 'N/A'}
                </p>
              </div>
            </div>
          </section>

          {/* Checks Summary Section */}
          <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-[#051923]">Screening Checks</h2>
            <CheckStatusTable checkStatuses={checkStatuses} />
          </section>

          {/* Upgrade CTA Section */}
          <section className="mb-8 rounded-lg border border-blue-200 bg-blue-50 p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-blue-100 p-2">
                <Lock className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-[#051923]">Unlock Full Report Details</h3>
                <p className="mt-1 text-sm text-gray-600">
                  View detailed findings, evidence files, source articles, and download the
                  pre-populated SRA compliance form. The full report includes:
                </p>
                <ul className="mt-2 list-inside list-disc text-sm text-gray-600">
                  <li>Complete screening log with AI analysis</li>
                  <li>Detailed sanctions match information</li>
                  <li>Adverse media article summaries and links</li>
                  <li>Evidence package download</li>
                  <li>Pre-populated SRA form</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer - Full Width */}
      <footer className="shrink-0 border-t bg-white px-8 py-4">
        <div className="flex items-center justify-end gap-6">
          <button
            type="button"
            onClick={() => navigate({ to: '/' })}
            className="font-inter text-[16px] font-semibold text-[#131313] underline"
          >
            Back to Dashboard
          </button>
          <Button
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={isDownloadingPdf}
            className="border-[#131313] text-[#131313] hover:bg-gray-50"
          >
            {isDownloadingPdf ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </>
            )}
          </Button>
          <Button onClick={onUpgrade} className="bg-[#131313] hover:bg-[#131313]/90">
            View Full Report
          </Button>
        </div>
      </footer>
    </div>
  );
}
